import { Row } from 'antd';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import PanelContentBreadcrumb from '@portal/components/PanelContentBreadcrumb/PanelContentBreadcrumb';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import useReduxState from '@portal/hooks/useReduxState';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LimitsActions } from '@portal/actions';
import { moneyFormat } from '@portal/utils/format';
import {
  TransactionLimitType,
  PeriodType,
  TransactionType,
  TransactionLimitStatusType,
} from '@portal/models/enumerators';
import LimitPixModal from '@portal/components/LimitPixModal/LimitPixModal';
import PixLimitSkeleton from './PixLimitSkeleton';
import * as MessageService from '~/services/message';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import { Chip } from '@mui/material';
import { UPDATE_LIMIT_STATUS } from '@portal/actions/actionTypes';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { useNavigation } from '@portal/hooks/useNavigation';

const PixLimit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const [visible, setVisible] = useState(false);
  const [currentLimit, setCurrentLimit] =
    useState<bank.TransactionLimit | null>(null);
  const {
    limit: { list, status },
  } = useReduxState();

  const [pinModal, setPinModal] = useState(false);
  const [permanentLimit, setPermanentLimit] =
    useState<bank.TransactionLimit | null>({
      amount: 0,
      limitType: TransactionLimitType.TRANSACTION,
      transactionType: TransactionType.PIX,
      periodType: PeriodType.DAILY,
    });

  const filteredList = list?.filter(
    (limit) => limit.transactionType === TransactionType.PIX
  );

  const isDisabled = (limit) => {
    if (
      limit.status === TransactionLimitStatusType.SEND_SOLICITATION ||
      limit.status === TransactionLimitStatusType.PENDING
    ) {
      return true;
    }
    return false;
  };

  const getLimitLabel = (limitType: TransactionLimitType) => {
    switch (limitType) {
      case TransactionLimitType.PERIOD:
        return translate('PAGES.HOME.PIX.PIX_LIMIT.LABELS.PERIOD_LIMIT');
      case TransactionLimitType.TRANSACTION:
        return translate('PAGES.HOME.PIX.PIX_LIMIT.LABELS.TRANSACTION_LIMIT');
      default:
        return '';
    }
  };
  const handleLimitUpdate = (
    newLimit: number,
    selectedLimit?: bank.TransactionLimit
  ) => {
    if (selectedLimit) {
      if (newLimit === 0) {
        MessageService.error('LIMIT_CANNOT_BE_ZERO');
        return;
      }

      const samePeriodLimit = filteredList.find(
        (limit) =>
          limit.periodType === selectedLimit.periodType &&
          limit.limitType !== selectedLimit.limitType
      );

      if (
        selectedLimit.limitType === TransactionLimitType.TRANSACTION &&
        newLimit > Number(samePeriodLimit?.amount)
      ) {
        MessageService.error('LIMIT_TRANSACTION_EXCEED_LIMIT_PERIOD');
        return;
      }
      if (
        selectedLimit.limitType === TransactionLimitType.PERIOD &&
        newLimit < Number(samePeriodLimit?.amount)
      ) {
        MessageService.error('LIMIT_PERIOD_EXCEED_LIMIT_TRANSACTION');
        return;
      }
      setPermanentLimit({
        limitType: selectedLimit.limitType,
        transactionType: selectedLimit.transactionType,
        amount: newLimit,
        periodType: selectedLimit.periodType,
      });
      setPinModal(true);
    }
  };

  const confirmNewLimit = async (pin: string) => {
    setPinModal(false);
    if (pin && permanentLimit) {
      const encryptedPin = await encrypt(pin);
      dispatch(
        LimitsActions.updateLimit(
          {
            ...permanentLimit,
            amount: permanentLimit.amount,
            transactionType: permanentLimit.transactionType,
          },
          encryptedPin
        )
      );
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      dispatch(LimitsActions.getAllLimits());
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (status === TransactionLimitStatusType.SUCCESS) {
      MessageService.success('PAGES.LIMITS.MESSAGE.ALTER_LIMIT_SUCCESS');
      dispatch({
        type: UPDATE_LIMIT_STATUS,
        payload: null,
      });
    }
    if (status === TransactionLimitStatusType.DENIED) {
      MessageService.error('PAGES.LIMITS.MESSAGE.ALTER_LIMIT_DENIED');
    }
    if (
      status === TransactionLimitStatusType.SEND_SOLICITATION ||
      status === TransactionLimitStatusType.PENDING
    ) {
      MessageService.warn('PAGES.LIMITS.MESSAGE.ALTER_LIMIT_PENDING');
      dispatch({
        type: UPDATE_LIMIT_STATUS,
        payload: null,
      });
    } else {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className="pix-limit">
      <PanelContentBreadcrumb
        items={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.HOME.PIX.PAGE_TITLE'),
            url: getRouteStackPath('PIX', 'PIX'),
          },
          {
            active: false,
            title: translate('PAGES.HOME.PIX.PIX_LIMIT.TITLE'),
          },
        ]}
      />
      <AdvancedPanelContentHeader
        testId="pix-limit-header"
        title={translate('PAGES.HOME.PIX.PIX_LIMIT.TITLE')}
        description={translate('PAGES.HOME.PIX.PIX_LIMIT.DESCRIPTION')}
        goBackAction={() => navigate.back()}
      />
      <div className="pix-limit__limits">
        <p className="pix-limit__body__header__text">
          {translate('PAGES.HOME.PIX.PIX_LIMIT.HEADER_TEXT')}
        </p>
        <ConfirmationModal
          visible={pinModal}
          setVisible={setPinModal}
          handleConfirmation={(pin: string) => confirmNewLimit(pin)}
        />
        {visible ? (
          <LimitPixModal
            title={translate('PAGES.HOME.PIX.PIX_LIMIT.MODAL.TITLE')}
            visible={visible}
            onCloseModal={() => {
              setVisible(false);
              setCurrentLimit(null);
            }}
            onHandleUpdate={(rawValue: number) =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              handleLimitUpdate(rawValue, currentLimit as any)
            }
          />
        ) : null}
        <h3>
          <strong>
            {translate('PAGES.HOME.PIX.PIX_LIMIT.LABELS.NIGHTTIME')}
          </strong>
        </h3>
        {filteredList ? (
          filteredList
            .filter((limits) => limits.periodType === PeriodType.NIGHTLY)
            .map((limit) => (
              <React.Fragment key={limit.id}>
                <Row
                  align="middle"
                  justify="space-between"
                  className="pix-limit__body__item-key"
                >
                  <div>
                    <p className="pix-limit__body__text">
                      {`${getLimitLabel(limit.limitType)}: ${moneyFormat(
                        limit.amount
                      )}`}
                    </p>
                  </div>
                  {(limit.status ===
                    TransactionLimitStatusType.SEND_SOLICITATION ||
                    limit.status === TransactionLimitStatusType.PENDING) && (
                    <Chip
                      label={translate(
                        'PAGES.LIMITS.MESSAGE.ALTER_LIMIT_PENDING_HEADER'
                      )}
                      variant="outlined"
                    />
                  )}
                  <Row align="middle">
                    <AdvancedButton
                      text={translate(
                        'PAGES.HOME.PIX.PIX_LIMIT.BUTTONS.DEFINE'
                      )}
                      onClick={() => {
                        setVisible(true);
                        setCurrentLimit(limit);
                      }}
                      disabled={isDisabled(limit)}
                    />
                  </Row>
                </Row>
              </React.Fragment>
            ))
        ) : (
          <PixLimitSkeleton />
        )}
        <h3>
          <strong>
            {translate('PAGES.HOME.PIX.PIX_LIMIT.LABELS.DAYTIME')}
          </strong>
        </h3>
        {filteredList ? (
          filteredList
            .filter((limits) => limits.periodType === PeriodType.DAILY)
            .map((limit) => (
              <React.Fragment key={limit.id}>
                <Row
                  align="middle"
                  justify="space-between"
                  className="pix-limit__body__item-key"
                >
                  <div>
                    <p className="pix-limit__body__text">
                      {`${getLimitLabel(limit.limitType)}: ${moneyFormat(
                        limit.amount
                      )}`}
                    </p>
                  </div>
                  {(limit.status ===
                    TransactionLimitStatusType.SEND_SOLICITATION ||
                    limit.status === TransactionLimitStatusType.PENDING) && (
                    <Chip
                      label={translate(
                        'PAGES.LIMITS.MESSAGE.ALTER_LIMIT_PENDING_HEADER'
                      )}
                      variant="outlined"
                    />
                  )}
                  <Row align="middle">
                    <AdvancedButton
                      text={translate(
                        'PAGES.HOME.PIX.PIX_LIMIT.BUTTONS.DEFINE'
                      )}
                      onClick={() => {
                        setVisible(true);
                        setCurrentLimit(limit);
                      }}
                      disabled={isDisabled(limit)}
                    />
                  </Row>
                </Row>
              </React.Fragment>
            ))
        ) : (
          <PixLimitSkeleton />
        )}
      </div>
    </div>
  );
};

export default PixLimit;
